import axios from "axios";
import Config from "../../../config";
import {
  callNotification,
  capitalizeFirstLetter,
  errorCode,
  getUTCOffset,
} from "../../constants";
import { handleApiError, handleAxiosError } from "../../utils/errorHandler";

import {
  setBannerImageUrlInLogosJson,
  setBrandUploadLoader,
  setLogoImageUrlInLogosJson,
  setLogoUrlInBrandConfig,
  setSelectedThemeState,
  setThemesListInState,
} from "./PersonalizationSettings";
import { setScreenLoader } from "../commandCenter/screens/screen";
import { fetchScreenData } from "../commandCenter/commandCenterApi";
import { setThemeId } from "../AuthParams";

export const getUploadedImageUrl = (value, type) => {
  return async (dispatch) => {
    if (value) {
      let body = {
        image_base64: value,
        utcoffset: getUTCOffset(),
      };
      let url = `${Config.config1.api_link}/ppt-report/upload-chart`;
      //   let url = `${Config?.config1?.api_link}/template/upload-image`;
      axios
        .post(url, body)
        .then((response) => {
          if (response?.data?.status === "successful") {
            if (type === "logo") {
              dispatch(setLogoUrlInBrandConfig(response?.data?.image_url));
              dispatch(setLogoImageUrlInLogosJson(response?.data?.image_url));
              callNotification(
                "Brand logo is updated successfully.",
                "success",
                40,
                "personalization-preview-notification personalization-preview-mask",
                true
              );
            } else if (type === "banner") {
              dispatch(setBannerImageUrlInLogosJson(response?.data?.image_url));
              callNotification(
                "Banner image is updated successfully.",
                "success",
                40,
                "personalization-preview-notification personalization-preview-mask",
                true
              );
            }
          } else if (response?.data?.status === "error") {
            dispatch(setLogoUrlInBrandConfig(null));

            // Custom error handlers to ensure error code 21 notifications are shown
            const customErrorHandlers = {
              21: (errorCodeValue, errorMessage, response, dispatch) => {
                // For error code 21, always show notification
                callNotification(
                  errorMessage || null,
                  "error",
                  40,
                  "personalization-preview-notification personalization-preview-mask",
                  true
                );
              },
              default: (errorCodeValue, errorMessage, response, dispatch) => {
                // For other error codes, show custom notification
                callNotification(
                  errorMessage || null,
                  "error",
                  40,
                  "personalization-preview-notification personalization-preview-mask",
                  true
                );
              },
            };

            handleApiError(response, dispatch, {
              suppressNotification: true,
              customErrorHandlers,
            });
          }
          dispatch(setBrandUploadLoader(false));
        })
        .catch((error) => {
          dispatch(setLogoUrlInBrandConfig(null));
          dispatch(setBrandUploadLoader(false));
          callNotification(
            null,
            "error",
            40,
            "personalization-preview-notification personalization-preview-mask",
            true
          );
          // Also handle with standard error handler but suppress notification to avoid duplicates
          handleAxiosError(error, dispatch, { suppressNotification: true });
        });
    } else {
    }
  };
};

// to save personalized settings
export const setThemeOnScreen = (authParams, themeId, applyToAll) => {
  let errorMessage;
  return async (dispatch) => {
    // dispatch(setScreenLoader(true));
    let body = {
      ptoken: authParams?.ptoken,
      theme_id: themeId,
      screen_id: applyToAll ? null : authParams?.screen_id,
      is_applied_to_all: applyToAll,
      utcoffset: getUTCOffset(),
    };
    let url = `${Config.config1.api_link}/command-center/apply-theme`;
    await axios
      .post(url, body)
      .then((response) => {
        if (response?.data?.status === "successful") {
          callNotification(
            `Theme applied successfully to ${applyToAll ? "all" : "this"} ${
              applyToAll ? "screens." : "screen."
            }`,
            "success",
            40,
            "personalization-preview-notification personalization-preview-mask",
            true
          );
          // dispatch(fetchScreenData(authParams, {}));
        } else if (response?.data?.status === "error") {
          // Custom error handlers to ensure error code 21 notifications are shown
          const customErrorHandlers = {
            21: (errorCodeValue, errorMessage, response, dispatch) => {
              // For error code 21, always show notification
              callNotification(
                errorMessage || null,
                "error",
                40,
                "personalization-preview-notification personalization-preview-mask",
                true
              );
            },
            default: (errorCodeValue, errorMessage, response, dispatch) => {
              // For other error codes, show custom notification
              callNotification(
                errorMessage || null,
                "error",
                40,
                "personalization-preview-notification personalization-preview-mask",
                true
              );
            },
          };

          handleApiError(response, dispatch, {
            suppressNotification: true,
            customErrorHandlers,
          });
        }
      })
      .catch((error) => {
        dispatch(setScreenLoader(false));

        // Also handle with standard error handler but suppress notification to avoid duplicates
        const errorResult = handleAxiosError(error, dispatch, {
          suppressNotification: true,
        });
        callNotification(
          errorResult.errorMessage || null,
          "error",
          40,
          "personalization-preview-notification personalization-preview-mask",
          true
        );
      });
  };
};

// to fetch and save theme list
export const getAndSaveThemeList = (authParams, newthemeId) => {
  let errorMessage;
  return async (dispatch) => {
    // dispatch(setScreenLoader(true));
    let body = {
      ptoken: authParams.ptoken,
      utcoffset: getUTCOffset(),
    };
    let url = `${Config.config1.api_link}/command-center/list-cc-themes`;
    await axios
      .post(url, body)
      .then((response) => {
        if (response?.status === 200) {
          // dispatch(setScreenLoader(false));
          if (response?.data) {
            // dispatch to save list in main state
            dispatch(
              setThemesListInState(response?.data?.length ? response?.data : [])
            );
            dispatch(setThemeId(response?.data?.length ? response?.data : []));
            dispatch(setSelectedThemeState(newthemeId));
          } else {
            // Handle API error response
            const errorResult = handleApiError(response, dispatch, {
              suppressNotification: true,
            });
            if (errorResult.hasError) {
              callNotification(
                errorResult.errorMessage || null,
                "error",
                40,
                "personalization-preview-notification personalization-preview-mask",
                true
              );
            }
          }
        }
      })
      .catch((error) => {
        // Also handle with standard error handler but suppress notification to avoid duplicates
        const errorResult = handleAxiosError(error, dispatch, {
          suppressNotification: true,
        });
        callNotification(
          errorResult.errorMessage || null,
          "error",
          40,
          "personalization-preview-notification personalization-preview-mask",
          true
        );
      });
  };
};

// to create new theme
export const createNewTheme = (authParams, settings, name, description) => {
  let errorMessage;
  // set local state to existing state inn list and save modified local state as new state

  return async (dispatch) => {
    let body = {
      ptoken: authParams?.ptoken,
      theme_name: capitalizeFirstLetter(
        name?.trim() || settings?.themeName?.trim()
      ),
      theme_description: description || settings?.themeDescription,
      theme_json: {
        CommandCenterLayout: settings.CommandCenterLayout,
        CommandCenterScreenBanner: settings.CommandCenterScreenBanner,
        CommandCenterScreenFooter: settings.CommandCenterScreenFooter,
        brandConfig: settings.brandConfig,
        paletteList: settings.paletteList,
      },
      saved_as: settings.savedAs || "Theme",
      logos_json: {
        banner_image: settings?.logosJson?.bannerImage,
        brand_logo: settings?.logosJson?.brandLogo,
      },
      utcoffset: getUTCOffset(),
    };
    let url = `${Config.config1.api_link}/command-center/create-theme`;
    await axios
      .post(url, body)
      .then((response) => {
        if (response?.data?.status === "successful") {
          callNotification(
            "New theme created successfully",
            "success",
            40,
            "personalization-preview-notification personalization-preview-mask",
            true
          );
          dispatch(
            getAndSaveThemeList(authParams, response?.data?.result?.theme_id)
          );
          dispatch(setThemeId(response?.data?.result?.theme_id));
        } else if (response?.data?.status === "error") {
          // Also handle with standard error handler but suppress notification to avoid duplicates
          const errorResult = handleApiError(response, dispatch, {
            suppressNotification: true,
          });
          callNotification(
            response?.data?.reason || errorResult.errorMessage || null,
            "error",
            40,
            "personalization-preview-notification personalization-preview-mask",
            true
          );
        }
      })
      .catch((error) => {
        dispatch(setScreenLoader(false));

        // Also handle with standard error handler but suppress notification to avoid duplicates
        const errorResult = handleAxiosError(error, dispatch, {
          suppressNotification: true,
        });
        callNotification(
          errorResult.errorMessage || null,
          "error",
          40,
          "personalization-preview-notification personalization-preview-mask",
          true
        );
      });
  };
};

// to update a theme
export const updateTheme = (authParams, settings, name, description) => {
  let errorMessage;
  return async (dispatch) => {
    let body = {
      ptoken: authParams?.ptoken,
      theme_id: settings.themeId,
      theme_name: name || settings.themeName,
      theme_description: description || settings.themeDescription,
      theme_json: {
        CommandCenterLayout: settings.CommandCenterLayout,
        CommandCenterScreenBanner: settings.CommandCenterScreenBanner,
        CommandCenterScreenFooter: settings.CommandCenterScreenFooter,
        brandConfig: settings.brandConfig,
        paletteList: settings.paletteList,
      },
      saved_as: settings.savedAs || "Theme",
      logos_json: settings.logosJson,
      utcoffset: getUTCOffset(),
    };
    let url = `${Config.config1.api_link}/command-center/update-theme`;
    await axios
      .post(url, body)
      .then((response) => {
        if (response?.data?.status === "successful") {
          dispatch(getAndSaveThemeList(authParams));
          callNotification(
            "Theme updated successfully",
            "success",
            40,
            "personalization-preview-notification personalization-preview-mask",
            true
          );
        } else if (response?.data?.status === "error") {
          // Also handle with standard error handler but suppress notification to avoid duplicates
          const errorResult = handleApiError(response, dispatch, {
            suppressNotification: true,
          });
          if (errorResult.hasError) {
            callNotification(
              errorResult.errorMessage || null,
              "error",
              40,
              "personalization-preview-notification personalization-preview-mask",
              true
            );
          }
        }
      })
      .catch((error) => {
        dispatch(setScreenLoader(false));

        // Also handle with standard error handler but suppress notification to avoid duplicates
        const errorResult = handleAxiosError(error, dispatch, {
          suppressNotification: true,
        });
        callNotification(
          errorResult.errorMessage || null,
          "error",
          40,
          "personalization-preview-notification personalization-preview-mask",
          true
        );
      });
  };
};

// to delete a theme
export const deleteTheme = (authParams, themeId) => {
  let errorMessage;
  return async (dispatch) => {
    let body = {
      ptoken: authParams?.ptoken,
      theme_id: themeId,
      utcoffset: getUTCOffset(),
    };
    let url = `${Config.config1.api_link}/command-center/delete-theme`;
    await axios
      .post(url, body)
      .then((response) => {
        if (response?.data?.status === "successful") {
          dispatch(getAndSaveThemeList(authParams));
          callNotification(
            "Theme deleted successfully",
            "success",
            40,
            "personalization-preview-notification personalization-preview-mask",
            true
          );
        } else if (response?.data?.status === "error") {
          // Also handle with standard error handler but suppress notification to avoid duplicates
          const errorResult = handleApiError(response, dispatch, {
            suppressNotification: true,
          });
          if (errorResult.hasError) {
            callNotification(
              errorResult.errorMessage || null,
              "error",
              40,
              "personalization-preview-notification personalization-preview-mask",
              true
            );
          }
        }
      })
      .catch((error) => {
        dispatch(setScreenLoader(false));

        // Also handle with standard error handler but suppress notification to avoid duplicates
        const errorResult = handleAxiosError(error, dispatch, {
          suppressNotification: true,
        });
        callNotification(
          errorResult.errorMessage || null,
          "error",
          40,
          "personalization-preview-notification personalization-preview-mask",
          true
        );
      });
  };
};
